import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, DatePicker, Empty, Space, Spin, Tag } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ExperimentOutlined, PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useParams, useSearchParams } from 'react-router-dom';

import {
  DATE_QUERY_PARAM,
  JST_TIMEZONE,
} from '../components/location-analysis/analysisConfig';
import {
  buildRawGnssFetchRange,
  formatSelectedDayQueryParam,
  parseSelectedDayQueryParam,
} from '../components/location-analysis/dateUtils';
import LocationAnalysisResultView from '../components/location-analysis/LocationAnalysisResultView';
import useLocationAnalysisDerivedData from '../components/location-analysis/useLocationAnalysisDerivedData';
import { useDevice } from '../contexts/DeviceContext';
import { usePageHeader } from '../contexts/PageHeaderContext';
import './LocationPage.css';

dayjs.extend(utc);
dayjs.extend(timezone);

const LocationPage: React.FC = () => {
  const { deviceId } = useParams<{ deviceId: string }>();
  const { devices, selectedDevice, loading: devicesLoading } = useDevice();
  const { setPageHeader } = usePageHeader();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDayQueryParam = searchParams.get(DATE_QUERY_PARAM);
  const [selectedDay, setSelectedDay] = useState<Dayjs>(() => {
    return parseSelectedDayQueryParam(selectedDayQueryParam) || dayjs().tz(JST_TIMEZONE);
  });

  const activeDevice = useMemo(() => {
    if (deviceId) {
      return devices.find((device) => device.id === deviceId) || null;
    }
    return selectedDevice;
  }, [deviceId, devices, selectedDevice]);

  const rawGnssFetchRange = useMemo(() => buildRawGnssFetchRange(selectedDay), [selectedDay]);

  const updateSelectedDayQueryParam = useCallback(
    (nextDay: Dayjs) => {
      const nextDateParam = formatSelectedDayQueryParam(nextDay);
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set(DATE_QUERY_PARAM, nextDateParam);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleSelectedDayChange = useCallback(
    (value: Dayjs | null) => {
      if (!value) {
        return;
      }
      const nextDay = value.tz(JST_TIMEZONE);
      setSelectedDay(nextDay);
      updateSelectedDayQueryParam(nextDay);
    },
    [updateSelectedDayQueryParam]
  );

  useEffect(() => {
    const queryDay = parseSelectedDayQueryParam(selectedDayQueryParam);
    if (!queryDay) {
      updateSelectedDayQueryParam(selectedDay);
      return;
    }
    const queryDateParam = formatSelectedDayQueryParam(queryDay);
    setSelectedDay((current) => {
      return formatSelectedDayQueryParam(current) === queryDateParam ? current : queryDay;
    });
  }, [selectedDay, selectedDayQueryParam, updateSelectedDayQueryParam]);

  const derived = useLocationAnalysisDerivedData({
    deviceId: activeDevice?.id,
    rangeStartIso: rawGnssFetchRange.startIso,
    rangeEndIso: rawGnssFetchRange.endIso,
  });

  const pageHeaderControls = useMemo(() => {
    if (!activeDevice) {
      return null;
    }
    return (
      <Space wrap>
        <DatePicker value={selectedDay} onChange={handleSelectedDayChange} allowClear={false} size="small" />
        <Button icon={<ReloadOutlined />} onClick={() => void derived.loadDerivedData()} size="small">
          再読込
        </Button>
        <Button
          type="primary"
          icon={<PlayCircleOutlined />}
          loading={derived.running || derived.rebuildJobRunning}
          onClick={() => void derived.runAnalysis()}
          size="small"
        >
          再解析
        </Button>
        <Tag icon={<ExperimentOutlined />}>{activeDevice.name}</Tag>
        <Tag>{selectedDay.tz(JST_TIMEZONE).format('YYYY-MM-DD')} JST</Tag>
      </Space>
    );
  }, [activeDevice, derived, handleSelectedDayChange, selectedDay]);

  useEffect(() => {
    setPageHeader({
      title: '位置情報',
      controls: pageHeaderControls,
    });
  }, [pageHeaderControls, setPageHeader]);

  useEffect(() => {
    return () => setPageHeader(null);
  }, [setPageHeader]);

  if (devicesLoading && !activeDevice) {
    return <Spin />;
  }

  if (!activeDevice) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="デバイスを選択してください" />;
  }

  return (
    <Spin spinning={derived.loading}>
      <Space direction="vertical" size="middle" className="location-page">
        {derived.rebuildJobRunning ? (
          <Alert
            type="info"
            showIcon
            message="解析ジョブ実行中"
            description={`job=${derived.rebuildJob?.job_id} status=${derived.rebuildJob?.status}`}
          />
        ) : null}
        {derived.rebuildJob?.status === 'failed' ? (
          <Alert
            type="error"
            showIcon
            message="解析ジョブ失敗"
            description={derived.rebuildJob.error_message || '-'}
          />
        ) : null}

        <LocationAnalysisResultView
          items={derived.timelineItems}
          staypoints={derived.staypoints}
          triplegs={derived.triplegs}
          trips={derived.trips}
          locations={derived.locations}
          visibleLocations={derived.visibleLocations}
          locationExtentBufferM={derived.locationExtentBufferM}
          locationNameMap={derived.locationNameMap}
          locationPoiMap={derived.locationPoiMap}
          poiCandidatesByLocationId={derived.poiCandidatesByLocationId}
          searchingPoiLocationId={derived.searchingPoiLocationId}
          linkingPoiLocationId={derived.linkingPoiLocationId}
          selectedTarget={derived.selectedDerivedTarget}
          onSelect={(target) => derived.setSelectedDerivedTarget(target)}
          onRenameLocation={derived.handleRenameLocation}
          onUpdateLocationSemanticType={derived.handleUpdateLocationSemanticType}
          onOverrideTriplegMode={derived.handleOverrideTriplegMode}
          onDeleteTripleg={derived.handleDeleteTripleg}
          onReassignStaypointLocation={derived.handleReassignStaypointLocation}
          onSearchLocationPoi={derived.handleSearchLocationPoi}
          onLinkLocationPoi={derived.handleLinkLocationPoi}
        />
      </Space>
    </Spin>
  );
};

export default LocationPage;
